'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EEGSessionRecord {
  id: string;
  sessionKey: string;
  subjectId: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
  avgCognitiveLoad: number | null;
  avgAttentionScore: number | null;
  avgSignalQuality: number | null;
  avgArtifactRate: number | null;
  avgThetaAlphaRatio: number | null;
  avgInterHemisphericCoherence: number | null;
  avgDeltaPower: number | null;
  avgThetaPower: number | null;
  avgAlphaPower: number | null;
  avgBetaPower: number | null;
  avgGammaPower: number | null;
  peakCognitiveLoad: number | null;
  peakAttentionScore: number | null;
  markerCount: number;
  label: string | null;
  notes: string | null;
  createdAt: string;
  // Joined data
  aiAnalysis?: EEGAIAnalysisRecord[];
  biomarkers?: EEGBiomarkerRecord[];
}

export interface EEGSnapshotRecord {
  id: string;
  sessionId: string;
  snapshotTime: number;
  cognitiveLoad: number | null;
  attentionScore: number | null;
  signalQuality: number | null;
  artifactRate: number | null;
  thetaAlphaRatio: number | null;
  interHemisphericCoherence: number | null;
  deltaPower: number | null;
  thetaPower: number | null;
  alphaPower: number | null;
  betaPower: number | null;
  gammaPower: number | null;
}

export interface EEGAIAnalysisRecord {
  id: string;
  sessionId: string;
  analysisType: string;
  modelUsed: string | null;
  analysisText: string;
  tbrValue: number | null;
  tarValue: number | null;
  darValue: number | null;
  engagementIndex: number | null;
  literatureContext: string | null;
  createdAt: string;
}

export interface EEGBiomarkerRecord {
  id: string;
  sessionId: string;
  biomarkerType: string;
  severity: string | null;
  severityScore: number | null;
  riskLevel: string | null;
  thetaAlphaRatio: number | null;
  interHemisphericCoherence: number | null;
  confidence: number | null;
  dataSource: string | null;
  tbrValue: number | null;
  tbrClassification: string | null;
  rawResponse: Record<string, unknown> | null;
  createdAt: string;
}

export interface SaveSessionParams {
  sessionKey: string;
  subjectId?: string;
  startedAt: Date;
  endedAt: Date;
  durationSeconds: number;
  snapshots: Array<{
    time: number;
    metrics: {
      cognitiveLoad: number;
      attentionScore: number;
      betaPower: number;
      alphaPower: number;
      thetaPower: number;
      thetaAlphaRatio: number;
      interHemisphericCoherence: number;
      signalQuality?: number;
      artifactRate?: number;
      deltaPower?: number;
      gammaPower?: number;
    };
  }>;
  markerCount: number;
  label?: string;
  notes?: string;
}

export interface SaveAIAnalysisParams {
  sessionId: string;
  analysisType: 'clinical_eeg' | 'cognitive_insights' | 'session_summary';
  modelUsed?: string;
  analysisText: string;
  tbrValue?: number;
  tarValue?: number;
  darValue?: number;
  engagementIndex?: number;
  literatureContext?: string;
}

export interface SaveBiomarkerParams {
  sessionId: string;
  biomarkerType: 'alzheimer' | 'adhd';
  severity?: string;
  severityScore?: number;
  riskLevel?: string;
  thetaAlphaRatio?: number;
  interHemisphericCoherence?: number;
  confidence?: number;
  dataSource?: string;
  tbrValue?: number;
  tbrClassification?: string;
  rawResponse?: Record<string, unknown>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function avg(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function peak(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.max(...values);
}

function isSchemaError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string };
  if (err.code && typeof err.code === 'string') {
    const cls = err.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (err.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return patterns.some((p) => p.test(err.message!));
  }
  return false;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const eegSessionService = {
  /**
   * Save a complete EEG session with all snapshots to Supabase.
   * Returns the created session ID.
   */
  async saveSession(params: SaveSessionParams): Promise<string | null> {
    const supabase = createClient();

    // Compute aggregate metrics from snapshots
    const snaps = params.snapshots;
    const cogLoads = snaps.map((s) => s.metrics.cognitiveLoad).filter(Boolean) as number[];
    const attentions = snaps.map((s) => s.metrics.attentionScore).filter(Boolean) as number[];
    const sigQuals = snaps.map((s) => s.metrics.signalQuality ?? 0).filter(Boolean) as number[];
    const artifacts = snaps.map((s) => s.metrics.artifactRate ?? 0).filter(Boolean) as number[];
    const tars = snaps.map((s) => s.metrics.thetaAlphaRatio).filter(Boolean) as number[];
    const coherences = snaps.map((s) => s.metrics.interHemisphericCoherence).filter(Boolean) as number[];
    const deltas = snaps.map((s) => s.metrics.deltaPower ?? 0).filter(Boolean) as number[];
    const thetas = snaps.map((s) => s.metrics.thetaPower).filter(Boolean) as number[];
    const alphas = snaps.map((s) => s.metrics.alphaPower).filter(Boolean) as number[];
    const betas = snaps.map((s) => s.metrics.betaPower).filter(Boolean) as number[];
    const gammas = snaps.map((s) => s.metrics.gammaPower ?? 0).filter(Boolean) as number[];

    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('eeg_sessions')
        .insert({
          session_key: params.sessionKey,
          subject_id: params.subjectId ?? 'anonymous',
          started_at: params.startedAt.toISOString(),
          ended_at: params.endedAt.toISOString(),
          duration_seconds: params.durationSeconds,
          avg_cognitive_load: avg(cogLoads),
          avg_attention_score: avg(attentions),
          avg_signal_quality: avg(sigQuals),
          avg_artifact_rate: avg(artifacts),
          avg_theta_alpha_ratio: avg(tars),
          avg_inter_hemispheric_coherence: avg(coherences),
          avg_delta_power: avg(deltas),
          avg_theta_power: avg(thetas),
          avg_alpha_power: avg(alphas),
          avg_beta_power: avg(betas),
          avg_gamma_power: avg(gammas),
          peak_cognitive_load: peak(cogLoads),
          peak_attention_score: peak(attentions),
          marker_count: params.markerCount,
          label: params.label ?? null,
          notes: params.notes ?? null,
        })
        .select('id')
        .single();

      if (sessionError) {
        if (isSchemaError(sessionError)) {
          console.error('Schema error saving EEG session:', sessionError.message);
          throw sessionError;
        } else {
          console.warn('EEG session insert error:', sessionError.message);
          return null;
        }
      }

      const sessionId = sessionData?.id;
      if (!sessionId) return null;

      // Insert snapshots in batches of 50
      if (snaps.length > 0) {
        const snapshotRows = snaps.map((s) => ({
          session_id: sessionId,
          snapshot_time: s.time,
          cognitive_load: s.metrics.cognitiveLoad,
          attention_score: s.metrics.attentionScore,
          signal_quality: s.metrics.signalQuality ?? null,
          artifact_rate: s.metrics.artifactRate ?? null,
          theta_alpha_ratio: s.metrics.thetaAlphaRatio,
          inter_hemispheric_coherence: s.metrics.interHemisphericCoherence,
          delta_power: s.metrics.deltaPower ?? null,
          theta_power: s.metrics.thetaPower,
          alpha_power: s.metrics.alphaPower,
          beta_power: s.metrics.betaPower,
          gamma_power: s.metrics.gammaPower ?? null,
        }));

        // Batch insert in chunks of 50
        for (let i = 0; i < snapshotRows.length; i += 50) {
          const chunk = snapshotRows.slice(i, i + 50);
          const { error: snapError } = await supabase
            .from('eeg_session_snapshots')
            .insert(chunk);
          if (snapError && isSchemaError(snapError)) {
            console.error('Schema error saving snapshots:', snapError.message);
          }
        }
      }

      return sessionId;
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('EEG session save failed:', err);
      return null;
    }
  },

  /**
   * Save AI analysis result linked to a session.
   */
  async saveAIAnalysis(params: SaveAIAnalysisParams): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('eeg_ai_analysis').insert({
        session_id: params.sessionId,
        analysis_type: params.analysisType,
        model_used: params.modelUsed ?? null,
        analysis_text: params.analysisText,
        tbr_value: params.tbrValue ?? null,
        tar_value: params.tarValue ?? null,
        dar_value: params.darValue ?? null,
        engagement_index: params.engagementIndex ?? null,
        literature_context: params.literatureContext ?? null,
      });
      if (error && isSchemaError(error)) {
        console.error('Schema error saving AI analysis:', error.message);
      }
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('AI analysis save failed:', err);
    }
  },

  /**
   * Save biomarker result (Alzheimer or ADHD) linked to a session.
   */
  async saveBiomarker(params: SaveBiomarkerParams): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('eeg_biomarker_results').insert({
        session_id: params.sessionId,
        biomarker_type: params.biomarkerType,
        severity: params.severity ?? null,
        severity_score: params.severityScore ?? null,
        risk_level: params.riskLevel ?? null,
        theta_alpha_ratio: params.thetaAlphaRatio ?? null,
        inter_hemispheric_coherence: params.interHemisphericCoherence ?? null,
        confidence: params.confidence ?? null,
        data_source: params.dataSource ?? null,
        tbr_value: params.tbrValue ?? null,
        tbr_classification: params.tbrClassification ?? null,
        raw_response: params.rawResponse ?? null,
      });
      if (error && isSchemaError(error)) {
        console.error('Schema error saving biomarker:', error.message);
      }
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('Biomarker save failed:', err);
    }
  },

  /**
   * Fetch recent sessions with optional AI analysis and biomarker data.
   */
  async fetchSessions(limit = 20): Promise<EEGSessionRecord[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('eeg_sessions')
        .select(`
          *,
          eeg_ai_analysis(*),
          eeg_biomarker_results(*)
        `)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching sessions:', error.message);
          throw error;
        }
        console.warn('Sessions fetch error:', error.message);
        return [];
      }

      return (data ?? []).map((row) => ({
        id: row.id,
        sessionKey: row.session_key,
        subjectId: row.subject_id,
        startedAt: row.started_at,
        endedAt: row.ended_at,
        durationSeconds: row.duration_seconds,
        avgCognitiveLoad: row.avg_cognitive_load,
        avgAttentionScore: row.avg_attention_score,
        avgSignalQuality: row.avg_signal_quality,
        avgArtifactRate: row.avg_artifact_rate,
        avgThetaAlphaRatio: row.avg_theta_alpha_ratio,
        avgInterHemisphericCoherence: row.avg_inter_hemispheric_coherence,
        avgDeltaPower: row.avg_delta_power,
        avgThetaPower: row.avg_theta_power,
        avgAlphaPower: row.avg_alpha_power,
        avgBetaPower: row.avg_beta_power,
        avgGammaPower: row.avg_gamma_power,
        peakCognitiveLoad: row.peak_cognitive_load,
        peakAttentionScore: row.peak_attention_score,
        markerCount: row.marker_count ?? 0,
        label: row.label,
        notes: row.notes,
        createdAt: row.created_at,
        aiAnalysis: (row.eeg_ai_analysis ?? []).map((a: Record<string, unknown>) => ({
          id: a.id,
          sessionId: a.session_id,
          analysisType: a.analysis_type,
          modelUsed: a.model_used,
          analysisText: a.analysis_text,
          tbrValue: a.tbr_value,
          tarValue: a.tar_value,
          darValue: a.dar_value,
          engagementIndex: a.engagement_index,
          literatureContext: a.literature_context,
          createdAt: a.created_at,
        })),
        biomarkers: (row.eeg_biomarker_results ?? []).map((b: Record<string, unknown>) => ({
          id: b.id,
          sessionId: b.session_id,
          biomarkerType: b.biomarker_type,
          severity: b.severity,
          severityScore: b.severity_score,
          riskLevel: b.risk_level,
          thetaAlphaRatio: b.theta_alpha_ratio,
          interHemisphericCoherence: b.inter_hemispheric_coherence,
          confidence: b.confidence,
          dataSource: b.data_source,
          tbrValue: b.tbr_value,
          tbrClassification: b.tbr_classification,
          rawResponse: b.raw_response,
          createdAt: b.created_at,
        })),
      }));
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('Sessions fetch failed:', err);
      return [];
    }
  },

  /**
   * Fetch snapshots for a specific session (for trend charts).
   */
  async fetchSnapshots(sessionId: string): Promise<EEGSnapshotRecord[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('eeg_session_snapshots')
        .select('*')
        .eq('session_id', sessionId)
        .order('snapshot_time', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.warn('Snapshots fetch error:', error.message);
        return [];
      }

      return (data ?? []).map((row) => ({
        id: row.id,
        sessionId: row.session_id,
        snapshotTime: row.snapshot_time,
        cognitiveLoad: row.cognitive_load,
        attentionScore: row.attention_score,
        signalQuality: row.signal_quality,
        artifactRate: row.artifact_rate,
        thetaAlphaRatio: row.theta_alpha_ratio,
        interHemisphericCoherence: row.inter_hemispheric_coherence,
        deltaPower: row.delta_power,
        thetaPower: row.theta_power,
        alphaPower: row.alpha_power,
        betaPower: row.beta_power,
        gammaPower: row.gamma_power,
      }));
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('Snapshots fetch failed:', err);
      return [];
    }
  },

  /**
   * Delete a session and all related data (cascades via FK).
   */
  async deleteSession(sessionId: string): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('eeg_sessions')
        .delete()
        .eq('id', sessionId);
      if (error && isSchemaError(error)) {
        console.error('Schema error deleting session:', error.message);
      }
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('Session delete failed:', err);
    }
  },
};
